// src/console/pages/races/raceAccess.ts — what an operator may do in a race.
//
// Five capabilities, seeded by MIG-R2 and granted explicitly (spec §6.9 —
// roles don't inherit new capabilities in prod). They are kept SEPARATE on
// purpose, the way useContentAccess keeps content.manage and content.publish
// apart: ops can run boosts and pause a race without being able to re-price
// the points, and compliance can exclude a racer without either.
//
// Presentational components never read these. Containers call useRaceAccess()
// and pass plain booleans down, so a preview can render any role.

export const RACE_CAPABILITY = {
  /** Read the console. Ops, marketing, finance, compliance, founder, super. */
  view: "campaign.points.view",
  /** Settings, boosts ≤3× and ≤24 h, pause / resume, ending (with a second approver). */
  manage: "campaign.points.manage",
  /** Publish rule sets and weights (dual control). CTO, CPO. */
  rules: "campaign.points.rules",
  /** Manual ± points (dual control above 100). Ops lead. */
  adjust: "campaign.points.adjust",
  /** Disqualify / restore a racer. Compliance. */
  exclude: "campaign.points.exclude",
} as const;

export interface RaceAccess {
  canView: boolean;
  canManage: boolean;
  canPublishRules: boolean;
  canAdjust: boolean;
  canExclude: boolean;
}

export const NO_RACE_ACCESS: RaceAccess = {
  canView: false,
  canManage: false,
  canPublishRules: false,
  canAdjust: false,
  canExclude: false,
};

/** Every capability — the founder / super-admin view. For previews and tests. */
export const FULL_RACE_ACCESS: RaceAccess = {
  canView: true,
  canManage: true,
  canPublishRules: true,
  canAdjust: true,
  canExclude: true,
};

/** View only — finance and compliance reading the race. For previews and tests. */
export const READ_ONLY_RACE_ACCESS: RaceAccess = {
  ...NO_RACE_ACCESS,
  canView: true,
};
